import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ICliente,
  IQueryParam,
  IProveedor,
  IFilter,
  TipoRecordatorio,
  IRecordatorio,
  ICreateRecordatorio,
  IUpdateRecordatorio,
  IActivo,
  CategoriaRecordatorio,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RecordatoriosService } from '../../../../auxiliares/servicios/http/recordatorios.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { LoginService } from '../../../login/login.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';

@Component({
  selector: 'app-crear-editar-recordatorio',
  templateUrl: './crear-editar-recordatorio.component.html',
  styleUrl: './crear-editar-recordatorio.component.scss',
  standalone: false,
})
export class CrearEditarRecordatorioComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<CrearEditarRecordatorioComponent>>(
    MatDialogRef,
    { optional: true },
  );
  private route = inject(ActivatedRoute);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  dialog = inject(MatDialog);
  private service = inject(RecordatoriosService);
  private dialogService = inject(DialogService);

  public id?: string;
  public title?: string;
  public clientePropio = LoginService.getCliente();
  public name = 'CrearEditarRecordatorioComponent';
  public form?: FormGroup;
  public tipo?: string[];
  public tipos: TipoRecordatorio[] = ['km', 'fecha'];
  public recordatorio: IRecordatorio;
  public activos?: IActivo[] = [];
  public activos$?: Subscription;

  public clienteElegido: ICliente;
  public categoria: CategoriaRecordatorio = 'Vehiculo';

  public subcategorias: string[] = [
    'Cambio de aceite y filtro',
    'Cambio de aceite de caja',
    'Cambio de líquido refrigerante',
    'Cambio de filtro de combustible',
    'Cambio de filtro de aire',
    'Cambio de filtro de habitáculo',
    'Cambio de batería',
    'Cambio de cubiertas',
    'Cambio de luces',
    'Cambio de líquido de frenos',
    'Cambio de pastillas de freno',
    'Cambio de bujías',
    'Otro',
  ];

  public loading = true;
  public tipoCliente = HelperService.getTipoCliente();
  public minDate = new Date();

  private createForm() {
    // Determinar el cliente por defecto basado en el tipo de usuario
    const clienteDefault =
      this.tipoCliente === 'Final'
        ? this.clientePropio._id
        : this.recordatorio?.idCliente;

    this.form = new FormGroup({
      categoria: new FormControl(this.categoria, Validators.required),
      tipo: new FormControl(this.recordatorio?.tipo, Validators.required),
      subcategoria: new FormControl(
        (this.recordatorio as any)?.subcategoria,
        Validators.required,
      ),
      idCliente: new FormControl(clienteDefault, Validators.required),
      idUsuario: new FormControl(this.recordatorio?.idUsuario ?? null),
      idActivo: new FormControl(
        this.recordatorio?.idActivo,
        Validators.required,
      ),
      fechaLimite: new FormControl(
        this.recordatorio?.fechaLimite
          ? new Date(this.recordatorio.fechaLimite)
          : null,
      ),
      kmLimite: new FormControl(
        this.recordatorio?.kmLimite,
        Validators.pattern('^[0-9]*$'),
      ),
      detallesDelMantenimiento: new FormControl(
        this.recordatorio?.detallesDelMantenimiento,
      ),
      repetible: new FormControl(this.recordatorio?.repetible || false),
      frecuenciaKm: new FormControl((this.recordatorio as any)?.frecuenciaKm),
      frecuenciaDia: new FormControl((this.recordatorio as any)?.frecuenciaDia),
    });
  }

  /**
   * Activo a preseleccionar cuando el alta viene del detalle de un vehículo.
   * Se aplica recién cuando llegan las opciones del select: ng-select no puede
   * mostrar un valor que todavía no está en `activos`.
   */
  private idActivoPendiente?: string;

  private aplicarActivoPendiente(): void {
    if (!this.idActivoPendiente || !this.form) return;
    if (!this.activos?.some((a) => a._id === this.idActivoPendiente)) return;
    this.form.patchValue({ idActivo: this.idActivoPendiente });
    this.idActivoPendiente = undefined;
  }

  private async listarActivos() {
    const filter: IFilter<IActivo> = {};
    if (this.clienteElegido?._id) {
      filter['idCliente'] = this.clienteElegido._id;
    }
    const query: IQueryParam = {
      page: 0,
      limit: 0,
      includeChildren: true,
      filter: JSON.stringify(filter),
    };

    if (this.categoria === 'Colectivo') {
      this.activos$?.unsubscribe();
      this.activos$ = this.lista2.colectivos(query).subscribe((data) => {
        this.activos = data.datos;
      });
    }
    if (this.categoria === 'Vehiculo') {
      this.activos$?.unsubscribe();
      this.activos$ = this.lista2.vehiculos(query).subscribe((data) => {
        this.activos = data.datos;
        this.aplicarActivoPendiente();
      });
    }
  }

  //
  public tieneKm(): boolean {
    return this.tipo?.includes('km') || false;
  }

  public tieneFecha(): boolean {
    return this.tipo?.includes('fecha') || false;
  }

  public repetible(): boolean {
    return this.form?.value.repetible;
  }

  public toggleTipo(valor: TipoRecordatorio) {
    const current: TipoRecordatorio[] = this.form?.get('tipo')?.value || [];
    const index = current.indexOf(valor);
    const next =
      index >= 0 ? current.filter((v) => v !== valor) : [...current, valor];
    this.form?.get('tipo')?.setValue(next);
  }

  public toggleOpcion() {
    if (this.tipo?.length == 0) {
      this.form.get('kmLimite').enable();
      this.form.get('fechaLimite').enable();
      this.form.patchValue({ repetible: false });
    } else {
      if (this.tipo?.includes('km')) {
        this.form.get('kmLimite').enable();
      } else {
        this.form.get('kmLimite').disable();
      }
      if (this.tipo?.includes('fecha')) {
        this.form.get('fechaLimite').enable();
      } else {
        this.form.get('fechaLimite').disable();
      }
      // Si no hay ningún tipo que soporte repetible, desactivar
      if (!this.tipo?.includes('km') && !this.tipo?.includes('fecha')) {
        this.form.patchValue({ repetible: false });
      }
    }
  }

  private async getRecordatorio(id: string) {
    this.recordatorio = await firstValueFrom(this.lista2.recordatorio(id));
  }

  public async onSubmit() {
    const res = await this.dialogService.confirm(
      `${this.title} Recordatorio`,
      `¿Querés ${this.title.toLowerCase()} el recordatorio?`,
    );
    if (!res) return;
    this.loading = true;
    try {
      const data = this.getData();
      if (data) {
        if (this.id) {
          // Update
          await this.service.update(this.id, data);
          this.helper.notifSuccess('Recordatorio actualizado');
          HelperService.volver();
        } else {
          // Create
          await this.service.create(data);
          this.helper.notifSuccess('Recordatorio creado');
          HelperService.volver();
        }
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  public getData(): ICreateRecordatorio | IUpdateRecordatorio {
    if (this.tieneFecha() && !this.repetible()) {
      const date = new Date(this.form?.value.fechaLimite);
      if (!date || isNaN(date.getTime())) {
        this.helper.notifError('Ingresá una fecha válida');
        return null;
      }
      if (date.getTime() <= Date.now()) {
        this.helper.notifError(
          'La fecha del recordatorio no puede ser menor o igual a la fecha de hoy',
        );
        return null;
      }
    }
    const data: ICreateRecordatorio | IUpdateRecordatorio = this.form?.value;
    return data;
  }

  public onClienteChange(cliente: ICliente): void {
    if (!cliente) {
      this.activos = [];
      return;
    }

    this.clienteElegido = cliente;

    this.activos = [];
    this.form?.patchValue({
      idActivo: null,
    });

    this.listarActivos();
  }

  async ngOnInit() {
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');
    this.categoria = params.get('categoria') as CategoriaRecordatorio;
    // Alta desde el detalle de un vehículo: el activo y su cliente llegan por
    // query param (ver ListadoRecordatoriosComponent.crear)
    const query = await firstValueFrom(this.route.queryParamMap);
    const idActivoPreseleccionado = query.get('idActivo');
    const idClientePreseleccionado = query.get('idCliente');

    this.title = this.id ? 'Editar Recordatorio' : 'Crear Recordatorio';

    // Si estamos editando, obtener los datos primero
    if (this.id) {
      await this.getRecordatorio(this.id);

      if (this.recordatorio?.idCliente) {
        this.clienteElegido = { _id: this.recordatorio.idCliente } as ICliente;
      }
      await this.listarActivos();
    } else {
      if (this.tipoCliente === 'Final') {
        // Si es cliente final, establecer su cliente y cargar sus datos
        this.clienteElegido = this.clientePropio;
        await this.listarActivos();
      } else if (idClientePreseleccionado) {
        // Hay que cargar los activos ANTES de preseleccionar: si no, el select
        // no tiene opciones y el valor patcheado no matchea nada
        this.clienteElegido = { _id: idClientePreseleccionado } as ICliente;
        await this.listarActivos();
      }
    }

    this.createForm();

    // Si es usuario final, establecer automáticamente el cliente
    if (this.tipoCliente === 'Final') {
      this.form.patchValue({
        idCliente: this.clientePropio?._id,
      });
    }

    if (!this.id && idActivoPreseleccionado) {
      if (idClientePreseleccionado) {
        this.form.patchValue({ idCliente: idClientePreseleccionado });
      }
      // Se aplica cuando lleguen las opciones del select (o ya, si están)
      this.idActivoPendiente = idActivoPreseleccionado;
      this.aplicarActivoPendiente();
    }

    if (this.id) {
      this.tipo = this.form.get('tipo').value;
      this.toggleOpcion();
    }

    // Suscribirse a cambios en el tipo
    this.form.get('tipo').valueChanges.subscribe(() => {
      this.tipo = this.form.get('tipo').value;
      this.toggleOpcion();
    });

    this.loading = false;
  }

  ngOnDestroy(): void {
    this.activos$?.unsubscribe();
  }
}
